/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";

/** A user row of the access panel, with the role currently assigned on this resource. */
type AccessUser = {
  userId?: string;
  roleId?: number | string;
  permissionName?: string;
  permissions?: number[];
  [key: string]: unknown;
};

/** One selectable folder role. */
type FolderRoleOption = {
  id?: number;
  name?: string;
  frontendName?: string;
  description?: string;
  permissions?: number[];
  [key: string]: unknown;
};
import { RootState } from "@Reducers/index";
import React, { useEffect, useState } from "react";
import { getFolderRoles, setDataForFolder } from "@Reducers/AccessPermissionReducer";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { useParams } from "react-router-dom";
import RecursiveConfirmationPopup from "../RecursiveConfirmationPopup";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { refreshFileManager } from "@Actions/fileUpload";
import Text from "@CubeComponents/html/Text/Text";
import { setModulePermissions } from "@Components/ModuleUsersAccess/constants";
import type { SetFolderRoleResponse } from "@ServerTypes/folders/folders.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

function AccessContent(props: {
  resourceId?: string;
  resourceType?: string;
  spaceId?: string;
  transmittalId?: string;
  /**
   * KNOWN BUG, preserved verbatim: this is a STRING (`directoryDataFileItemSchema.path` is
   * `z.string()`, "Breadcrumb path string"), and `folderName` below does
   * `pathofFolder?.[pathofFolder?.length - 1]` — on a string that yields its last CHARACTER, not the
   * last path segment. A path of "Design/Drawings" gives "s".
   */
  pathofFolder?: string;
  parentId?: string;
  transmittalHasFileId?: string;
  accessPermission?: Record<string, boolean>;
}) {
  const params = useParams();
  const dispatch = useDispatch();
  const {
    resourceId,
    transmittalId,
    transmittalHasFileId,
    spaceId,
    pathofFolder,
    resourceType = params.workspaceId,
  } = props;
  const folderName = pathofFolder?.[pathofFolder?.length - 1];
  const setFolderRoles = useSelector((state: RootState) => state.setFolderRoles);
  const setFolderData = useSelector(
    (state: RootState) => state.setFolderData,
  ) as unknown as Partial<SetFolderRoleResponse>;

  // const [isUserDropdownActive, setIsUserDropdownActive] = useState(false);
  const [spaceData, setSpaceData] = useState<AccessUser[]>([]);
  const [isResourceTypeFolder, setIsResourceTypeFolder] = useState(false);
  const [isRecursiveValue, setIsRecursiveValue] = useState(false);
  // Explicit type argument: `roleId` is stored as the numeric role id by `setUserPermissionData`,
  // which the `""` initialiser alone would infer as `string`. Initial values unchanged.
  const [updateUserInfo, setUpdateUserInfo] = useState<{
    userId: string;
    roleId: string | number;
    permissionName: string;
  }>({
    userId: "",
    roleId: "",
    permissionName: "",
  });
  const [updatedFolderRoles, setUpdatedFolderRoles] = useState<FolderRoleOption[]>([]);

  // used to fetch spaceUserName and there roles
  useEffect(() => {
    if (resourceId && spaceId) {
      dispatch(
        getFolderRoles({
          // Erased: these arrive as plain-string props; `getFolderRolesRequest` declares them
          // `objectIdString`. Route-supplied 24-hex ids; values unchanged.
          resourceId: resourceId as ObjectIdString,
          spaceId: spaceId as ObjectIdString,
          transmittalId: transmittalId as ObjectIdString | undefined,
          transmittalHasFileId: transmittalHasFileId as ObjectIdString | undefined,
        }),
      );
    }
  }, [resourceId, spaceId, transmittalHasFileId, transmittalId]);

  useEffect(() => {
    const names = resourceType!.split("_");
    const module = names[names.length - 1];
    const res = setModulePermissions(`${module}S`);
    setUpdatedFolderRoles([...res]);
  }, [resourceType, folderName]);

  // used for creating proper data for UI.
  useEffect(() => {
    const data: AccessUser[] = [];
    setFolderRoles.forEach((item1: AccessUser) => {
      updatedFolderRoles.forEach((item2: FolderRoleOption) =>
        item1.roleId === "" && (data.length === 0 || data[data.length - 1]!.userId !== item1.userId)
          ? data.push({ permissionName: "", ...item1 })
          : item1.roleId === item2.id
            ? data.push({ permissionName: item2.frontendName, ...item1 })
            : null,
      );
    });

    setSpaceData(data);
  }, [setFolderRoles, updatedFolderRoles]);

  // used when setting folder roles data
  useEffect(() => {
    if (setFolderData.status && updateUserInfo.permissionName !== "") {
      const userData = spaceData;
      const index = userData.findIndex((item) => item.userId === updateUserInfo.userId);
      userData[index]!.permissionName = updateUserInfo.permissionName;
      userData[index]!.roleId = updateUserInfo.roleId;
      setSpaceData(userData);
      // setIsUserDropdownActive(false);
      setUpdateUserInfo({ userId: "", permissionName: "", roleId: "" });
      dispatch(refreshFileManager(true));
    }
  }, [setFolderData.status]);

  const setUserPermissionData = (permissionName: string, roleId: number, userId: string) => {
    setUpdateUserInfo((prevState) => ({
      ...prevState,
      userId,
      permissionName,
      roleId,
    }));

    if (props.resourceType === "RESOURCE_TYPE_FOLDER") {
      setIsResourceTypeFolder(true);
    } else {
      dispatch(
        setDataForFolder({
          resourceId: transmittalHasFileId || resourceId,
          roleId,
          userId,
          spaceId,
          transmittalHasFileId,
          transmittalId,
          resourceType: props.resourceType,
        }),
      );
    }
  };

  const setUserPermissionForFolder = () => {
    dispatch(
      setDataForFolder({
        resourceId,
        roleId: updateUserInfo.roleId,
        userId: updateUserInfo.userId,
        spaceId,
        transmittalHasFileId,
        transmittalId,
        resourceType: props.resourceType,
        isRecursive: isRecursiveValue,
      }),
    );
    setIsResourceTypeFolder(false);
  };

  const toggleIsRecursive = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsRecursiveValue(event.target.value === "overRide");
  };

  const closeIsResourceTypeFolderPopUp = () => {
    setIsResourceTypeFolder(false);
  };

  const itemTemplate = (data: FolderRoleOption) => {
    if (data.id) {
      return (
        <div className="flex flex-row items-center px-4 py-2 gap-4">
          <div>
            <IconComponent name={data.name} width="1.5rem" height="1.5rem" />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <Text className="text-sm font-semibold">{data.frontendName}</Text>
              <Text className="text-sm" sx={{ whiteSpace: "normal" }}>
                {data.description}
              </Text>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="p-4 text-xs">
        <Text
          style={{
            wordBreak: "break-word",
            whiteSpace: "normal",
            display: "block",
          }}
        >
          {data.frontendName}
        </Text>
      </div>
    );
  };
  const userAvatar = (user: AccessUser) => <UserNameTagComponent id={user.userId} />;

  return (
    <>
      <div className="flex max-h-[31.25rem] flex-col items-center justify-between overflow-auto text-xs">
        <div className="flex max-h-[18.75rem] w-full flex-col justify-between overflow-auto">
          {spaceData.map((user: AccessUser) => (
            <div className="flex w-full items-center justify-between" key={user.userId}>
              <div className="flex items-center">
                <Text>{userAvatar(user)}</Text>
              </div>
              <div className="p-2" data-testid="folderFileRole">
                <SelectDropDown
                  id={`userRole_${user.userId}`}
                  renderOption={itemTemplate}
                  onChange={(option: FolderRoleOption | FolderRoleOption[] | null) =>
                    setUserPermissionData(
                      (option as FolderRoleOption).frontendName!,
                      (option as FolderRoleOption).id!,
                      user.userId!,
                    )
                  }
                  value={updatedFolderRoles.filter((item: FolderRoleOption) => item.id === user.roleId)[0] || {}}
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  fields={{
                    text: "frontendName",
                    value: "id",
                  }}
                  menuItemSx={{ padding: 0, height: "auto" }}
                  options={updatedFolderRoles}
                  buttonWidth="9.375rem"
                  popupAlign="right"
                  popperWidth={400}
                  maxHeight={400}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {isResourceTypeFolder && (
        <RecursiveConfirmationPopup
          header="permissions"
          handleSubmit={setUserPermissionForFolder}
          handleClose={closeIsResourceTypeFolderPopUp}
          toggleIsRecursive={toggleIsRecursive}
          module="folder"
          headerTitle={undefined}
          isRecursive={!!isRecursiveValue}
          recursiveOverrideWarningMessage={
            "This action will override permissions in all existing Files in this Folder."
          }
        />
      )}
    </>
  );
}

export default AccessContent;
